import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { StarRating } from "./StarRating";
import { ReviewCard } from "./ReviewCard";
import { WriteCompanyReviewModal } from "./WriteCompanyReviewModal";
import { EditReviewModal } from "./EditReviewModal";
import { ReportModal } from "@/components/reports/ReportModal";
import { ReportReviewModal } from "@/components/reviews/ReportReviewModal";
import { Star, Flag, Info } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  BrutalSelect,
  BrutalSelectTrigger,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";

interface CompanyReviewsSectionProps {
  companyId: string;
  companyName: string;
}

export const CompanyReviewsSection = ({
  companyId,
  companyName,
}: CompanyReviewsSectionProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [reviews, setReviews] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [sortBy, setSortBy] = useState<"recent" | "helpful" | "rating_high" | "rating_low">("recent");
  // State for reporting individual reviews
  const [reportReviewId, setReportReviewId] = useState<string | null>(null);
  const [reportReviewTitle, setReportReviewTitle] = useState<string>("");
  const [isReportReviewModalOpen, setIsReportReviewModalOpen] = useState(false);

  // State for editing own reviews
  const [editReview, setEditReview] = useState<any>(null);
  const [isEditReviewModalOpen, setIsEditReviewModalOpen] = useState(false);

  const openEditReviewModal = (review: any) => {
    setEditReview(review);
    setIsEditReviewModalOpen(true);
  };

  const closeEditReviewModal = () => {
    setEditReview(null);
    setIsEditReviewModalOpen(false);
  };

  const openReportReviewModal = (review: any) => {
    setReportReviewId(review.id);
    setReportReviewTitle(review.title);
    setIsReportReviewModalOpen(true);
  };

  const closeReportReviewModal = () => {
    setReportReviewId(null);
    setReportReviewTitle("");
    setIsReportReviewModalOpen(false);
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [hasReviewed, setHasReviewed] = useState(false);

  useEffect(() => {
    loadReviews();
    loadStats();
    if (user) {
      checkIfUserReviewed();
    }
  }, [companyId, user, sortBy]);

  const loadReviews = async () => {
    try {
      // Step 1: Load reviews without join
      let query = supabase
        .from("reviews")
        .select("*")
        .eq("company_id", companyId)
        .eq("admin_approved", true);

      if (sortBy === "recent") {
        query = query.order("created_at", { ascending: false });
      } else if (sortBy === "helpful") {
        query = query.order("helpful_count", { ascending: false });
      } else if (sortBy === "rating_high") {
        query = query.order("rating", { ascending: false });
      } else if (sortBy === "rating_low") {
        query = query.order("rating", { ascending: true });
      }

      const { data, error } = await query;
      if (error) throw error;

      let reviewsData = data || [];

      // Step 2: Load reviewer names in batch
      if (reviewsData.length > 0) {
        const userIds = [...new Set(reviewsData.map(r => r.user_id))];
        const { data: reviewerProfiles } = await supabase
          .from("profiles")
          .select("user_id, first_name, last_name, profile_image")
          .in("user_id", userIds);

        // Map names to reviews
        const reviewerMap = new Map(reviewerProfiles?.map(p => [p.user_id, p]) || []);
        reviewsData = reviewsData.map(review => ({
          ...review,
          reviewer: reviewerMap.get(review.user_id) || null
        }));
      }

      // Apply search filter
      if (searchQuery) {
        reviewsData = reviewsData.filter((review) =>
          review.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          review.comment.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      setReviews(reviewsData);
    } catch (error) {
      console.error("Error loading reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await supabase.rpc("calculate_company_rating", {
        p_company_id: companyId,
      });

      if (data && data.length > 0) {
        setStats(data[0]);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const checkIfUserReviewed = async () => {
    if (!user) return;
    
    try {
      const { data } = await supabase
        .from("reviews")
        .select("id")
        .eq("company_id", companyId)
        .eq("user_id", user.id)
        .maybeSingle();

      setHasReviewed(!!data);
    } catch (error) {
      console.error("Error checking user review:", error);
    }
  };

  const handleReviewSuccess = () => {
    loadReviews();
    loadStats();
    setHasReviewed(true);
  };

  // Trigger search when searchQuery changes
  useEffect(() => {
    if (!loading) {
      loadReviews();
    }
  }, [searchQuery]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Header with title, stats, and write button */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 gap-4">
        <div>
          <div className="flex justify-between items-start">
            <h2 className="font-heading text-3xl font-bold mb-2">
              {t('reviews.title')} ({stats?.total_reviews || 0})
            </h2>
            {/* Mobile only: report icon */}
            {user && (
              <button
                onClick={() => setShowReportModal(true)}
                className="sm:hidden p-2 text-muted-foreground hover:text-destructive transition-colors"
                aria-label={t('reports.reportProfile')}
              >
                <Flag className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Mobile only: button under title */}
          {user && !hasReviewed && (
            <BrutalButton onClick={() => setShowWriteModal(true)} className="sm:hidden mb-4">
              {t('reviews.writeReview')}
            </BrutalButton>
          )}

          {stats && stats.average_rating && (
            <div className="flex items-center gap-2">
              <span className="text-4xl font-bold mr-2">
                {stats.average_rating}
              </span>
              <StarRating rating={stats.average_rating} size="lg" readonly />
              
              <Popover>
                <PopoverTrigger asChild>
                  <button className="ml-2 p-1.5 hover:bg-muted transition-colors">
                    <Info className="w-4 h-4 text-muted-foreground hover:text-dark" />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="start" className="w-64 bg-card border-[3px] border-dark shadow-[6px_6px_0px_0px] shadow-dark p-4">
                  <p className="font-heading font-bold uppercase text-sm mb-3">{t('reviews.ratingDistribution')}</p>
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((stars) => (
                      <div key={stars} className="flex items-center gap-2">
                        <span className="text-sm font-bold w-8">{stars} ★</span>
                        <div className="flex-1 h-3 bg-muted relative border-2 border-dark">
                          <div
                            className="h-full bg-primary"
                            style={{
                              width: `${
                                stats.total_reviews > 0
                                  ? ((stats.rating_breakdown?.[stars] || 0) /
                                      stats.total_reviews) *
                                    100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-6 text-right">
                          {stats.rating_breakdown?.[stars] || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        <div className="hidden sm:flex items-center gap-2">
          {user && !hasReviewed && (
            <BrutalButton onClick={() => setShowWriteModal(true)}>
              {t('reviews.writeReview')}
            </BrutalButton>
          )}
          {user && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setShowReportModal(true)}
                    className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                    aria-label={t('reports.reportProfile')}
                  >
                    <Flag className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('reports.reportProfile')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Review Filters */}
      <div className="flex gap-2 mb-6">
        <BrutalSelect value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
          <BrutalSelectTrigger className="w-auto min-w-[180px]">
            <BrutalSelectValue />
          </BrutalSelectTrigger>
          <BrutalSelectContent>
            <BrutalSelectItem value="recent">{t('reviews.mostRecent')}</BrutalSelectItem>
            <BrutalSelectItem value="helpful">{t('reviews.mostHelpful')}</BrutalSelectItem>
            <BrutalSelectItem value="rating_high">{t('reviews.highestRating')}</BrutalSelectItem>
            <BrutalSelectItem value="rating_low">{t('reviews.lowestRating')}</BrutalSelectItem>
          </BrutalSelectContent>
        </BrutalSelect>

        <BrutalInput
          type="text"
          placeholder={t('reviews.searchReviews')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
      </div>

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="text-center py-12 bg-surface border-[3px] border-dark">
          <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">
            {t('reviews.noReviewsYet')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onVote={loadReviews}
              onReport={() => openReportReviewModal(review)}
              currentUserId={user?.id}
              onEdit={openEditReviewModal}
            />
          ))}
        </div>
      )}

      {/* Write Review Modal */}
      <WriteCompanyReviewModal
        isOpen={showWriteModal}
        onClose={() => setShowWriteModal(false)}
        companyId={companyId}
        companyName={companyName}
        onSuccess={handleReviewSuccess}
      />

      {/* Edit Review Modal */}
      {editReview && (
        <EditReviewModal
          isOpen={isEditReviewModalOpen}
          onClose={closeEditReviewModal}
          review={editReview}
          onSuccess={() => {
            loadReviews();
            loadStats();
          }}
        />
      )}

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        companyId={companyId}
        targetName={companyName}
      />

      {/* Report Review Modal */}
      <ReportReviewModal
        reviewId={reportReviewId || ""}
        reviewTitle={reportReviewTitle}
        isOpen={isReportReviewModalOpen}
        onClose={closeReportReviewModal}
      />
    </div>
  );
};
